import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Briefcase, Building2, User, X } from "lucide-react";
import { inputSurfaceClassName } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";

/** Participante do convite: e-mail obrigatório; nome quando veio do CRM */
export interface MeetingInvitee {
  email: string;
  name?: string;
}

export interface MeetingParticipantLead {
  id: string;
  name: string;
  email: string | null;
  companies?: { id: string; name: string } | null;
  /** Negócios vinculados ao lead (pode haver vários). */
  deals?: Array<{ id: string; title: string; updated_at?: string | null }> | null;
}

function companyNameFromLead(lead: MeetingParticipantLead): string | null {
  const n = lead.companies?.name?.trim();
  return n || null;
}

/** Um título para exibir: prioriza o deal mais recentemente atualizado. */
function primaryDealTitle(lead: MeetingParticipantLead): string | null {
  const arr = lead.deals;
  if (!arr?.length) return null;
  const sorted = [...arr].sort((a, b) => {
    const ta = a.updated_at ? new Date(a.updated_at).getTime() : 0;
    const tb = b.updated_at ? new Date(b.updated_at).getTime() : 0;
    return tb - ta;
  });
  const t = sorted[0]?.title?.trim();
  return t || null;
}

interface MeetingParticipantsFieldProps {
  id?: string;
  label?: string;
  invitees: MeetingInvitee[];
  onChange: (invitees: MeetingInvitee[]) => void;
  /** Lista local (modo legado). Com `onSearchQueryChange`, use `searchResults` no servidor. */
  leads?: MeetingParticipantLead[];
  leadsLoading?: boolean;
  /** Chamado a cada mudança no campo — habilita busca no servidor (nome/e-mail em todos os leads da org). */
  onSearchQueryChange?: (query: string) => void;
  /** Resultados da busca no servidor (filtrados por org na query pai). */
  searchResults?: MeetingParticipantLead[];
  searchLoading?: boolean;
  /** Se omitido, o texto de ajuda depende do modo (busca no servidor vs. lista local). */
  helperText?: string;
  disabled?: boolean;
  className?: string;
}

function normalize(str: string): string {
  return str.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
}

function matchesSearch(text: string, search: string): boolean {
  if (!search.trim()) return true;
  return normalize(text).includes(normalize(search));
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function highlightMatch(text: string, query: string, onPrimaryRow?: boolean): ReactNode {
  const q = query.trim();
  if (!q) return text;
  const upperText = text.toUpperCase();
  const upperQ = q.toUpperCase();
  const i = upperText.indexOf(upperQ);
  if (i < 0) return text;
  return (
    <>
      {text.slice(0, i)}
      <mark
        className={cn(
          "rounded-sm px-0.5",
          onPrimaryRow
            ? "bg-yellow-300 text-primary"
            : "bg-amber-200/90 text-neutral-950 dark:bg-amber-900/45 dark:text-amber-50",
        )}
      >
        {text.slice(i, i + upperQ.length)}
      </mark>
      {text.slice(i + upperQ.length)}
    </>
  );
}

function chipLabel(inv: MeetingInvitee): string {
  if (inv.name?.trim()) return `${inv.name.trim()} (${inv.email})`;
  return inv.email;
}

export function MeetingParticipantsField({
  id = "meeting-participants",
  label = "Participantes do evento",
  invitees,
  onChange,
  leads = [],
  leadsLoading = false,
  onSearchQueryChange,
  searchResults = [],
  searchLoading = false,
  helperText,
  disabled,
  className,
}: MeetingParticipantsFieldProps) {
  const serverSearch = Boolean(onSearchQueryChange);
  const resolvedHelper =
    helperText ??
    (serverSearch
      ? "A busca consulta nome e e-mail em todos os leads da organização. Você também pode digitar um e-mail e pressionar Enter."
      : "Busque contatos por nome ou e-mail na lista carregada, ou digite um e-mail e pressione Enter.");
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const selectedEmails = useMemo(
    () => new Set(invitees.map((i) => i.email.trim().toLowerCase())),
    [invitees],
  );

  const filteredLeads = useMemo(() => {
    const base = serverSearch ? searchResults : leads;
    return base.filter((l) => {
      if (!l.email?.trim()) return false;
      if (selectedEmails.has(l.email.trim().toLowerCase())) return false;
      if (serverSearch) return true;
      const hay = `${l.name} ${l.email}`;
      return matchesSearch(hay, query);
    });
  }, [serverSearch, searchResults, leads, query, selectedEmails]);

  const listLoading = serverSearch ? searchLoading : leadsLoading;

  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  useEffect(() => {
    setHighlightedIndex(0);
  }, [query, filteredLeads.length]);

  const addInvitee = useCallback(
    (inv: MeetingInvitee) => {
      const email = inv.email.trim().toLowerCase();
      if (!EMAIL_RE.test(email)) return;
      if (invitees.some((i) => i.email.trim().toLowerCase() === email)) return;
      onChange([...invitees, { ...inv, email }]);
      setQuery("");
      setOpen(false);
      inputRef.current?.focus();
    },
    [invitees, onChange],
  );

  const removeInvitee = useCallback(
    (email: string) => {
      onChange(invitees.filter((i) => i.email !== email));
    },
    [invitees, onChange],
  );

  const tryCommitEmail = useCallback(() => {
    const trimmed = query.trim().replace(/,$/, "");
    if (!trimmed) return;
    if (EMAIL_RE.test(trimmed)) {
      addInvitee({ email: trimmed });
    }
  }, [query, addInvitee]);

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Backspace" && !query && invitees.length > 0) {
      e.preventDefault();
      removeInvitee(invitees[invitees.length - 1].email);
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      if (!open) setOpen(true);
      setHighlightedIndex((i) => Math.min(i + 1, Math.max(0, filteredLeads.length - 1)));
      return;
    }
    if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightedIndex((i) => Math.max(0, i - 1));
      return;
    }
    if (e.key === "Escape") {
      setOpen(false);
      return;
    }
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      if (open && filteredLeads.length > 0 && highlightedIndex >= 0 && highlightedIndex < filteredLeads.length) {
        const lead = filteredLeads[highlightedIndex];
        if (lead.email) addInvitee({ email: lead.email, name: lead.name });
        return;
      }
      tryCommitEmail();
    }
  };

  const showList = open && (query.trim().length > 0 || listLoading);

  return (
    <div className={cn("space-y-1.5", className)}>
      <Label htmlFor={id}>{label}</Label>
      <div ref={containerRef} className="relative">
        <div
          className={cn(
            "flex min-h-11 w-full flex-wrap items-center gap-1.5",
            inputSurfaceClassName,
            "focus-within:outline-none focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2",
            disabled && "pointer-events-none cursor-not-allowed opacity-50",
          )}
          onClick={() => inputRef.current?.focus()}
        >
          {invitees.map((inv) => (
            <span
              key={inv.email}
              className="inline-flex max-w-full items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5 text-xs text-primary"
            >
              <span className="min-w-0 truncate">{chipLabel(inv)}</span>
              <button
                type="button"
                className="shrink-0 rounded-full p-0.5 text-primary/80 hover:bg-primary/15 hover:text-destructive"
                onMouseDown={(e) => e.preventDefault()}
                onClick={(e) => {
                  e.stopPropagation();
                  removeInvitee(inv.email);
                }}
                aria-label={`Remover ${inv.email}`}
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
          <input
            ref={inputRef}
            id={id}
            type="text"
            inputMode="email"
            autoComplete="off"
            disabled={disabled}
            value={query}
            onChange={(e) => {
              const v = e.target.value;
              setQuery(v);
              setOpen(true);
              onSearchQueryChange?.(v);
            }}
            onFocus={() => setOpen(true)}
            onKeyDown={onKeyDown}
            onBlur={() => {
              const trimmed = query.trim();
              if (trimmed && EMAIL_RE.test(trimmed) && !invitees.some((i) => i.email.toLowerCase() === trimmed.toLowerCase())) {
                addInvitee({ email: trimmed });
              }
            }}
            placeholder={invitees.length === 0 ? "Nome ou e-mail do participante..." : ""}
            className="min-w-[160px] flex-1 border-0 bg-transparent p-0 text-base shadow-none outline-none placeholder:text-muted-foreground focus-visible:ring-0 md:text-sm"
            aria-autocomplete="list"
            aria-expanded={showList}
            aria-controls={`${id}-listbox`}
          />
        </div>

        {showList && (
          <div
            id={`${id}-listbox`}
            role="listbox"
            className="absolute z-50 mt-1 max-h-[min(320px,50vh)] w-full overflow-y-auto rounded-md border bg-popover p-1 text-popover-foreground shadow-md"
          >
            {listLoading ? (
              <p className="py-6 text-center text-sm text-muted-foreground">Carregando contatos...</p>
            ) : query.trim().length === 0 ? (
              <p className="py-4 px-2 text-center text-xs text-muted-foreground">Digite para buscar contatos</p>
            ) : filteredLeads.length === 0 ? (
              <p className="py-4 px-2 text-center text-xs text-muted-foreground">
                Nenhum contato encontrado. Digite um e-mail válido e pressione Enter para adicionar.
              </p>
            ) : (
              filteredLeads.map((lead, idx) => {
                const active = idx === highlightedIndex;
                const company = companyNameFromLead(lead);
                const dealTitle = primaryDealTitle(lead);
                return (
                  <div
                    key={lead.id}
                    role="option"
                    tabIndex={-1}
                    aria-selected={active}
                    onMouseDown={(e) => e.preventDefault()}
                    onMouseEnter={() => setHighlightedIndex(idx)}
                    onClick={() => lead.email && addInvitee({ email: lead.email, name: lead.name })}
                    className={cn(
                      "group flex w-full cursor-pointer select-none items-start gap-2 rounded-sm px-2 py-2 text-left text-sm outline-none transition-colors focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
                      active
                        ? "bg-primary text-primary-foreground"
                        : "text-neutral-950 hover:bg-muted hover:!text-black dark:text-neutral-100 dark:hover:!text-neutral-100",
                    )}
                  >
                    <User
                      className={cn(
                        "mt-0.5 h-4 w-4 shrink-0",
                        active
                          ? "text-primary-foreground/90"
                          : "text-muted-foreground group-hover:!text-neutral-800 dark:group-hover:!text-neutral-300",
                      )}
                    />
                    <span className="min-w-0 flex-1 space-y-1">
                      <span
                        className={cn(
                          "block truncate font-semibold uppercase tracking-tight",
                          active
                            ? "text-primary-foreground"
                            : "text-neutral-950 group-hover:!text-black dark:text-neutral-100 dark:group-hover:!text-neutral-100",
                        )}
                      >
                        {highlightMatch(lead.name, query, active)}
                      </span>
                      {lead.email && (
                        <span
                          className={cn(
                            "block truncate text-xs",
                            active
                              ? "text-primary-foreground/85"
                              : "text-muted-foreground group-hover:!text-neutral-800 dark:group-hover:!text-neutral-300",
                          )}
                        >
                          ({lead.email})
                        </span>
                      )}
                      {(company || dealTitle) && (
                        <div
                          className={cn(
                            "flex flex-col gap-0.5 border-t pt-1 text-[11px] leading-tight",
                            active
                              ? "border-primary-foreground/25"
                              : "border-border/60 group-hover:border-neutral-300 dark:group-hover:border-neutral-600",
                          )}
                        >
                          {company && (
                            <span className="flex min-w-0 items-center gap-1">
                              <Building2
                                className={cn(
                                  "h-3 w-3 shrink-0",
                                  active
                                    ? "text-primary-foreground/80"
                                    : "text-muted-foreground group-hover:!text-neutral-700 dark:group-hover:!text-neutral-400",
                                )}
                                aria-hidden
                              />
                              <span
                                className={cn(
                                  "truncate",
                                  active
                                    ? "text-primary-foreground/90"
                                    : "text-muted-foreground group-hover:!text-neutral-900 dark:group-hover:!text-neutral-200",
                                )}
                              >
                                <span className="font-medium">Empresa</span>
                                <span
                                  className={cn(
                                    active
                                      ? "text-primary-foreground/75"
                                      : "text-muted-foreground/90 group-hover:!text-neutral-700 dark:group-hover:!text-neutral-400",
                                  )}
                                >
                                  {" · "}
                                </span>
                                {company}
                              </span>
                            </span>
                          )}
                          {dealTitle && (
                            <span className="flex min-w-0 items-center gap-1">
                              <Briefcase
                                className={cn(
                                  "h-3 w-3 shrink-0",
                                  active
                                    ? "text-primary-foreground/80"
                                    : "text-muted-foreground group-hover:!text-neutral-700 dark:group-hover:!text-neutral-400",
                                )}
                                aria-hidden
                              />
                              <span
                                className={cn(
                                  "truncate",
                                  active
                                    ? "text-primary-foreground/90"
                                    : "text-muted-foreground group-hover:!text-neutral-900 dark:group-hover:!text-neutral-200",
                                )}
                              >
                                <span className="font-medium">Negócio</span>
                                <span
                                  className={cn(
                                    active
                                      ? "text-primary-foreground/75"
                                      : "text-muted-foreground/90 group-hover:!text-neutral-700 dark:group-hover:!text-neutral-400",
                                  )}
                                >
                                  {" · "}
                                </span>
                                {dealTitle}
                              </span>
                            </span>
                          )}
                        </div>
                      )}
                    </span>
                  </div>
                );
              })
            )}
          </div>
        )}
      </div>
      {resolvedHelper && <p className="text-xs text-muted-foreground">{resolvedHelper}</p>}
    </div>
  );
}
